import { maskNric } from '@healthhub/react-ui';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { SwitchProfileTypeType } from '@shared/constants/constants';
import { SwitchProfileType } from '@shared/constants/constants';
import type { RelatedPersonProfile } from '@shared/services/caregivers/types/getRelatedPeople.types';
import type { FederatedUserProfile } from '@shared/services/personalHealth/types/getFederatedUserProfile.types';
import type { ProfileFull } from '@shared/types/user.types';
import { getDisplayName } from '@shared/utils/displayName';

import ProfileItem from './ProfileItem';

const { Text } = Typography;

export interface IProfileListingProps {
  careDependants?: RelatedPersonProfile[];
  childrens?: RelatedPersonProfile[];
  handleSelectProfile: (id: string, sourceCode: string, name: string, nric: string, relationship?: string) => void;
  others?: RelatedPersonProfile[];
  showSelfInSwitchProfile?: boolean;
  switchProfileType: SwitchProfileTypeType;
  titleText?: string;
  translationModule?: string;
  userProfile?: FederatedUserProfile | ProfileFull;
}

const ProfileListing = ({
  careDependants,
  childrens,
  handleSelectProfile,
  others,
  showSelfInSwitchProfile,
  switchProfileType = SwitchProfileType.ALL,
  titleText = 'profileList.selectProfile',
  translationModule,
  userProfile,
}: IProfileListingProps) => {
  const { t } = useTranslation(translationModule);
  const userDisplayName = getDisplayName(userProfile);

  if (!careDependants || !childrens || !userProfile) return null;

  return (
    <div className="flex flex-col gap-4">
      <Text className="text-base text-functional-black">{t(titleText)}</Text>
      <div className="flex flex-col gap-5">
        {!!(
          ((switchProfileType === SwitchProfileType.ALL ||
            switchProfileType === SwitchProfileType.SELF ||
            showSelfInSwitchProfile) &&
            switchProfileType !== SwitchProfileType.FILTERED) ||
          (switchProfileType === SwitchProfileType.FILTERED && showSelfInSwitchProfile)
        ) && (
          <div className="flex flex-col gap-2.5">
            <Text className="text-base font-bold text-functional-black">{t('profileList.myself')}</Text>
            <ProfileItem
              name={userDisplayName}
              onClick={() => handleSelectProfile('', '', userDisplayName, maskNric((userProfile as ProfileFull)?.Nric))}
              photo={userProfile?.Photo?.Data ?? ''}
            />
          </div>
        )}
        {childrens.length > 0 &&
          (switchProfileType === SwitchProfileType.ALL ||
            switchProfileType === SwitchProfileType.CHILDREN ||
            switchProfileType === SwitchProfileType.FILTERED) && (
            <div className="flex flex-col gap-2.5">
              <Text className="text-base font-bold text-functional-black">{t('profileList.myChildren')}</Text>
              <div className="flex flex-col gap-2">
                {childrens.map((item) => (
                  <ProfileItem
                    age={item.Age}
                    gender={item.Gender}
                    key={item.Prn}
                    name={item.DisplayName}
                    onClick={() =>
                      handleSelectProfile(item.Prn, 'CH', item.DisplayName, item.MaskedNric, item.RelationShip)
                    }
                    photo={item.Photo.Data}
                  />
                ))}
              </div>
            </div>
          )}
        {careDependants.length > 0 &&
          (switchProfileType === SwitchProfileType.ALL ||
            switchProfileType === SwitchProfileType.FAMILY ||
            switchProfileType === SwitchProfileType.FILTERED) && (
            <div className="flex flex-col gap-2.5">
              <Text className="text-base font-bold text-functional-black">{t('profileList.familyAndFriends')}</Text>
              <div className="flex flex-col gap-2">
                {careDependants.map((item) => (
                  <ProfileItem
                    age={item.Age}
                    gender={item.Gender}
                    key={item.Prn}
                    name={item.DisplayName}
                    onClick={() =>
                      handleSelectProfile(item.Prn, 'FH', item.DisplayName, item.MaskedNric, item.RelationShip)
                    }
                    photo={item.Photo.Data}
                  />
                ))}
              </div>
            </div>
          )}
        {!!others &&
          others.length > 0 &&
          (switchProfileType === SwitchProfileType.ALL ||
            switchProfileType === SwitchProfileType.FAMILY ||
            switchProfileType === SwitchProfileType.FILTERED) && (
            <div className="flex flex-col gap-2.5">
              <Text className="text-base font-bold text-functional-black">{t('profileList.others')}</Text>
              <div className="flex flex-col gap-2">
                {others.map((item) => (
                  <ProfileItem
                    age={item?.Age}
                    gender={item?.Gender}
                    key={item?.Prn}
                    name={item?.DisplayName}
                    onClick={() =>
                      handleSelectProfile(item.Prn, 'OTHER', item.DisplayName, item.MaskedNric, item.RelationShip)
                    }
                    photo={item.Photo.Data}
                  />
                ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default ProfileListing;
